"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { ArrowLeft, Plus, X, Star, LinkIcon, ImageIcon } from "lucide-react"
import { productsService, categoriesService } from "../../services/firebase"
import type { Product } from "../../store/slices/productsSlice"
import LoadingSpinner from "../../components/common/LoadingSpinner"
import toast from "react-hot-toast"
import type { VariantColor, VariantSize, ProductVariant } from "../../types/variants"
import { slugify, generateMatrixVariants, computeVariantId } from "../../utils/variants"

interface Category {
  id: string
  name: string
  slug: string
  isActive: boolean
  tags?: string[]
}

interface WardrobeVariant {
  id: string
  name: string
  price: number
  isActive: boolean
}

interface ProductFormData {
  name: string
  price: number
  originalPrice?: number
  description: string
  categories: string[]
  stock: number
  material?: string
  warranty?: string
  isFeatured: boolean
  isNewArrival: boolean
  isBestSeller: boolean
  dimensions?: {
    length: number
    width: number
    height: number
  }
  enableWardrobeVariants?: boolean
  enableCustomSize?: boolean
  customSizeMultiplier?: number
}

const PREDEFINED_WARDROBE_SIZES = [
  { name: "Small (2x3 ft)", defaultPrice: 15000 },
  { name: "Medium (3x4 ft)", defaultPrice: 25000 },
  { name: "Large (4x6 ft)", defaultPrice: 45000 },
  { name: "Extra Large (6x8 ft)", defaultPrice: 75000 },
]

const AddProduct: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingCategories, setIsLoadingCategories] = useState(true)
  const [imageUrls, setImageUrls] = useState<string[]>([""])
  const [mainImageIndex, setMainImageIndex] = useState<number>(0)
  const [features, setFeatures] = useState<string[]>([""])
  const [tags, setTags] = useState<string[]>([""])
  const [deliverablePincodes, setDeliverablePincodes] = useState<string[]>([])
  const [pincodeInput, setPincodeInput] = useState("")

  // Categories
  const [allCategories, setAllCategories] = useState<Category[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  // Regular variants state
  const [enableVariants, setEnableVariants] = useState(false)
  const [variantSizes, setVariantSizes] = useState<VariantSize[]>([
    { label: "Small" },
    { label: "Medium" },
    { label: "Large" },
    { label: "XL" },
  ])
  const [variantColors, setVariantColors] = useState<VariantColor[]>([])
  const [variants, setVariants] = useState<ProductVariant[]>([])

  const [enableWardrobeVariants, setEnableWardrobeVariants] = useState(false)
  const [enableCustomSize, setEnableCustomSize] = useState(false)
  const [customSizeMultiplier, setCustomSizeMultiplier] = useState(1250)
  const [wardrobeVariants, setWardrobeVariants] = useState<WardrobeVariant[]>([])

  // Color-specific images via URLs
  const [colorImageInputs, setColorImageInputs] = useState<Record<string, string>>({}) // temp inputs per color name

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ProductFormData>({
    defaultValues: {
      categories: [],
      isFeatured: false,
      isNewArrival: false,
      isBestSeller: false,
    },
  })

  const watchedCategories = watch("categories") || []

  useEffect(() => {
    loadCategories()
  }, [])

  useEffect(() => {
    if (JSON.stringify(selectedCategories) !== JSON.stringify(watchedCategories)) {
      setSelectedCategories(watchedCategories)
    }
  }, [watchedCategories])

  useEffect(() => {
    // Whenever sizes or colors change and variants are enabled, regenerate matrix (preserve existing data)
    if (enableVariants) {
      const combo = generateMatrixVariants(
        variantSizes,
        variantColors.map((c) => ({ name: c.name, hex: c.hex })),
        variants,
      ) as ProductVariant[]
      setVariants(combo.map((v) => ({ ...v, id: computeVariantId(v.size, v.color) })))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableVariants, variantSizes, variantColors])

  useEffect(() => {
    if (enableWardrobeVariants && wardrobeVariants.length === 0) {
      setWardrobeVariants(
        PREDEFINED_WARDROBE_SIZES.map((size, index) => ({
          id: `wardrobe-${index}`,
          name: size.name,
          price: size.defaultPrice,
          isActive: true,
        })),
      )
    }
  }, [enableWardrobeVariants])

  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true)
      const categories = await categoriesService.getCategories()

      if (!categories || categories.length === 0) {
        toast.error("No categories found. Please add categories first.")
        setAllCategories([])
        return
      }

      const activeCategories = categories
        .filter((cat: any) => cat && cat.isActive)
        .map((cat: any) => {
          const slug =
            cat.slug ||
            cat.name
              ?.toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^a-z0-9-]/g, "")
          if (!slug) return null
          return {
            ...cat,
            slug,
            tags: Array.isArray(cat.tags) ? cat.tags : [],
          }
        })
        .filter(Boolean) as Category[]

      setAllCategories(activeCategories)
      if (activeCategories.length === 0) {
        toast.error("No active categories found. Please add active categories first.")
      }
    } catch (error) {
      console.error("Error loading categories:", error)
      toast.error("Failed to load categories. Please try again.")
      setAllCategories([])
    } finally {
      setIsLoadingCategories(false)
    }
  }

  const addImageUrl = useCallback(() => {
    setImageUrls((prevImageUrls) => {
      if (prevImageUrls.length < 5) {
        return [...prevImageUrls, ""]
      } else {
        toast.error("Maximum 5 images allowed")
        return prevImageUrls
      }
    })
  }, [])

  const updateImageUrl = useCallback((index: number, url: string) => {
    setImageUrls((prev) => {
      const newUrls = [...prev]
      newUrls[index] = url
      return newUrls
    })
  }, [])

  const removeImageUrl = useCallback(
    (index: number) => {
      if (imageUrls.length <= 1) {
        toast.error("At least one image URL is required")
        return
      }
      setImageUrls((prev) => prev.filter((_, i) => i !== index))
      setMainImageIndex((prev) => {
        if (prev >= imageUrls.length - 1) {
          return Math.max(0, imageUrls.length - 2)
        } else if (index <= prev && prev > 0) {
          return prev - 1
        }
        return prev
      })
    },
    [imageUrls.length, imageUrls],
  )

  const setAsMainImage = useCallback((index: number) => {
    setMainImageIndex(index)
  }, [])

  const updateFeature = useCallback((index: number, value: string) => {
    setFeatures((prev) => prev.map((feature, i) => (i === index ? value : feature)))
  }, [])
  const removeFeature = useCallback((index: number) => {
    setFeatures((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const addTag = useCallback(() => {
    setTags((prev) => [...prev, ""])
  }, [])
  const updateTag = useCallback((index: number, value: string) => {
    setTags((prev) => prev.map((tag, i) => (i === index ? value : tag)))
  }, [])
  const removeTag = useCallback((index: number) => {
    setTags((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const validateImageUrl = (url: string): boolean => {
    if (!url || typeof url !== "string") return false
    try {
      new URL(url)
      return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url)
    } catch {
      return false
    }
  }

  const validatePincode = (pincode: string): boolean => /^\d{6}$/.test(pincode)

  const handleCategoryToggle = useCallback(
    (categorySlug: string) => {
      if (!categorySlug) {
        toast.error("Invalid category selection")
        return
      }
      setSelectedCategories((prev) => {
        const newSelection = prev.includes(categorySlug)
          ? prev.filter((slug) => slug !== categorySlug)
          : [...prev, categorySlug]
        setValue("categories", newSelection, { shouldValidate: true })
        return newSelection
      })
    },
    [setValue],
  )

  // Regular variants handlers
  const addSize = () => setVariantSizes((prev) => [...prev, { label: "" }])
  const updateSizeLabel = (index: number, value: string) =>
    setVariantSizes((prev) => prev.map((s, i) => (i === index ? { ...s, label: value } : s)))
  const removeSize = (index: number) => setVariantSizes((prev) => prev.filter((_, i) => i !== index))

  const toggleCustomDimensions = (index: number) => {
    setVariantSizes((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, dimensions: s.dimensions ? undefined : { length: undefined, width: undefined, height: undefined } }
          : s,
      ),
    )
  }
  const updateSizeDimension = (index: number, key: "length" | "width" | "height", value: number | undefined) =>
    setVariantSizes((prev) =>
      prev.map((s, i) => (i === index ? { ...s, dimensions: { ...(s.dimensions || {}), [key]: value } } : s)),
    )

  const addColor = () => setVariantColors((prev) => [...prev, { name: "", hex: "#cccccc", images: [] }])
  const updateColor = (idx: number, updates: Partial<VariantColor>) =>
    setVariantColors((prev) => prev.map((c, i) => (i === idx ? { ...c, ...updates } : c)))
  const removeColor = (idx: number) => setVariantColors((prev) => prev.filter((_, i) => i !== idx))

  const addColorImageUrl = (colorName: string) => {
    const url = (colorImageInputs[colorName] || "").trim()
    if (!validateImageUrl(url)) {
      toast.error("Please enter a valid image URL (jpg, png, webp, svg)")
      return
    }
    setVariantColors((prev) =>
      prev.map((c) => (slugify(c.name) === slugify(colorName) ? { ...c, images: [...(c.images || []), url] } : c)),
    )
    setColorImageInputs((prev) => ({ ...prev, [colorName]: "" }))
  }

  const removeColorImageUrl = (colorName: string, index: number) => {
    setVariantColors((prev) =>
      prev.map((c) =>
        slugify(c.name) === slugify(colorName) ? { ...c, images: (c.images || []).filter((_, i) => i !== index) } : c,
      ),
    )
  }

  const updateVariantField = (id: string, key: "stock" | "priceModifier" | "sku" | "isActive", value: any) => {
    setVariants((prev) => prev.map((v) => (v.id === id ? { ...v, [key]: value } : v)))
  }

  const addWardrobeVariant = () => {
    const newVariant: WardrobeVariant = {
      id: `wardrobe-${Date.now()}`,
      name: "",
      price: 0,
      isActive: true,
    }
    setWardrobeVariants((prev) => [...prev, newVariant])
  }

  const updateWardrobeVariant = (index: number, updates: Partial<WardrobeVariant>) => {
    setWardrobeVariants((prev) => prev.map((variant, i) => (i === index ? { ...variant, ...updates } : variant)))
  }

  const removeWardrobeVariant = (index: number) => {
    setWardrobeVariants((prev) => prev.filter((_, i) => i !== index))
  }

  const onSubmit = async (data: ProductFormData) => {
    try {
      const validImageUrls = imageUrls.filter((url) => url.trim() !== "" && validateImageUrl(url))
      if (validImageUrls.length === 0) {
        toast.error("Please add at least one valid image URL")
        return
      }

      if (deliverablePincodes.length === 0) {
        toast.error("Please add at least one deliverable pincode")
        return
      }
      const invalidPincodes = deliverablePincodes.filter((pc) => !validatePincode(pc))
      if (invalidPincodes.length > 0) {
        toast.error(`Invalid pincodes: ${invalidPincodes.join(", ")}. Pincodes must be 6 digits.`)
        return
      }

      if (!selectedCategories || selectedCategories.length === 0) {
        toast.error("Please select at least one category")
        return
      }

      if (!data.name?.trim()) {
        toast.error("Product name is required")
        return
      }
      if (!data.description?.trim()) {
        toast.error("Product description is required")
        return
      }
      if (!data.price || data.price <= 0) {
        toast.error("Valid price is required")
        return
      }
      if (data.stock == null || data.stock < 0) {
        toast.error("Valid stock quantity is required")
        return
      }

      // Validate variant setup
      let preparedVariants: ProductVariant[] | undefined = undefined
      let preparedVariantColors: VariantColor[] | undefined = undefined
      let preparedVariantSizes: VariantSize[] | undefined = undefined
      let totalVariantStock = 0

      if (enableVariants) {
        const cleanedSizes = variantSizes.filter((s) => s.label && s.label.trim().length > 0)
        const cleanedColors = variantColors
          .filter((c) => c.name && c.name.trim().length > 0 && /^#([0-9A-F]{3}){1,2}$/i.test(c.hex || "#ccc"))
          .map((c) => ({ ...c, slug: slugify(c.name) }))

        if (cleanedSizes.length === 0 || cleanedColors.length === 0) {
          toast.error("Please add at least one size and one color for variants")
          return
        }

        preparedVariantSizes = cleanedSizes
        preparedVariantColors = cleanedColors

        // Ensure full matrix is present
        const matrix = generateMatrixVariants(
          cleanedSizes,
          cleanedColors.map((c) => ({ name: c.name, hex: c.hex })),
          variants,
        ) as ProductVariant[]

        preparedVariants = matrix.map((v) => ({
          ...v,
          id: computeVariantId(v.size, v.color),
          colorHex: v.colorHex,
          stock: Number(v.stock || 0),
          priceModifier: Number(v.priceModifier || 0),
          isActive: v.isActive !== false,
        }))

        totalVariantStock = preparedVariants.reduce((sum, v) => sum + (v.stock || 0), 0)
      }

      if (enableWardrobeVariants) {
        const validWardrobeVariants = wardrobeVariants.filter((v) => v.name.trim() && v.price > 0)
        if (validWardrobeVariants.length === 0) {
          toast.error("Please add at least one valid wardrobe variant with name and price")
          return
        }
      }

      setIsLoading(true)

      const categoryNames = selectedCategories
        .map((catSlug) => {
          const category = allCategories.find((cat) => cat.slug === catSlug)
          return category?.name || catSlug
        })
        .filter(Boolean)

      const validFeatures = features.filter((f) => f && f.trim() !== "")
      const validTags = tags.filter((t) => t && t.trim() !== "")

      const tagSources = [
        validTags,
        selectedCategories,
        categoryNames.map((name) => name.toLowerCase().replace(/\s+/g, "-")),
        categoryNames.map((name) => name.toLowerCase()),
        data.name
          .toLowerCase()
          .split(/\s+/)
          .filter((w) => w.length > 2),
      ]
      const comprehensiveTags = tagSources
        .flat()
        .filter((tag) => tag && typeof tag === "string" && tag.trim().length > 0)
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag, index, arr) => arr.indexOf(tag) === index)

      const baseStock = enableVariants ? totalVariantStock : Number(data.stock)

      const productData: any = {
        name: data.name.trim(),
        price: Number(data.price),
        originalPrice: data.originalPrice ? Number(data.originalPrice) : undefined,
        finalPrice: data.originalPrice && data.originalPrice > data.price ? Number(data.price) : Number(data.price),
        discount:
          data.originalPrice && data.originalPrice > data.price
            ? Math.round(((Number(data.originalPrice) - Number(data.price)) / Number(data.originalPrice)) * 100)
            : 0,
        description: data.description.trim(),
        stock: baseStock,
        material: data.material?.trim() || undefined,
        warranty: data.warranty?.trim() || undefined,

        categories: selectedCategories,
        categoryNames,
        tags: comprehensiveTags,

        images: validImageUrls,
        image: validImageUrls[mainImageIndex] || validImageUrls[0],
        thumbnail: validImageUrls[mainImageIndex] || validImageUrls[0],

        features: validFeatures,
        deliverablePincodes: deliverablePincodes,
        pincodes: deliverablePincodes,

        rating: 0,
        reviews: 0,
        averageRating: 0,
        totalRatings: 0,
        commentCount: 0,
        brand: "Anmol Furniture",

        category: selectedCategories.length > 0 ? selectedCategories[0] : "",
        categoryPath: selectedCategories.length > 0 ? `/categories/${selectedCategories[0]}` : "",

        isFeatured: Boolean(data.isFeatured),
        isNewArrival: Boolean(data.isNewArrival),
        isBestSeller: Boolean(data.isBestSeller),
        isActive: true,

        dimensions:
          data.dimensions?.length || data.dimensions?.width || data.dimensions?.height
            ? {
                length: data.dimensions.length ? Number(data.dimensions.length) : undefined,
                width: data.dimensions.width ? Number(data.dimensions.width) : undefined,
                height: data.dimensions.height ? Number(data.dimensions.height) : undefined,
              }
            : undefined,

        engagement: {
          views: 0,
          wishlistCount: 0,
          cartCount: 0,
          orders: 0,
        },

        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),

        // Variants
        variants: enableVariants ? preparedVariants : undefined,
        variantColors: enableVariants ? preparedVariantColors : undefined,
        variantSizes: enableVariants ? preparedVariantSizes : undefined,
      }

      let preparedWardrobeVariants: WardrobeVariant[] | undefined
      if (enableWardrobeVariants) {
        preparedWardrobeVariants = wardrobeVariants
          .filter((v) => v.name.trim() && v.price > 0)
          .map((v) => ({
            ...v,
            name: v.name.trim(),
            price: Number(v.price),
            isActive: v.isActive !== false,
          }))
      }

      // Handle regular variants
      if (enableVariants && variants.length > 0) {
        const validVariants = variants.filter((v) => v.size && v.color)
        if (validVariants.length > 0) {
          productData.variants = validVariants
          productData.variantColors = variantColors.filter((c) => c.name.trim() && c.hex)
          productData.variantSizes = variantSizes.filter((s) => s.label.trim())
          productData.stock = validVariants.reduce((sum, v) => sum + (v.stock || 0), 0)
        }
      } else {
        productData.stock = Number(data.stock || 0)
      }

      if (enableWardrobeVariants && preparedWardrobeVariants && preparedWardrobeVariants.length > 0) {
        productData.wardrobeVariants = preparedWardrobeVariants
        productData.enableWardrobeVariants = true
        productData.enableCustomSize = enableCustomSize
        productData.customSizeMultiplier = customSizeMultiplier
      }

      Object.keys(productData).forEach((key) => {
        // @ts-ignore
        if (productData[key] === undefined) {
          // @ts-ignore
          delete productData[key]
        }
      })

      const productId = await productsService.addProduct(productData)
      if (!productId) {
        throw new Error("Failed to get product ID after creation")
      }

      toast.success(`Product "${data.name}" added successfully!`)
      navigate("/admin/products", {
        state: {
          message: `Product "${data.name}" was added successfully${enableVariants ? " with variants" : ""}.`,
        },
      })
    } catch (error) {
      console.error("Error adding product:", error)
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred"
      toast.error(`Failed to add product: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  const addPincode = useCallback(() => {
    const trimmedPincode = pincodeInput.trim()
    if (!trimmedPincode) {
      toast.error("Please enter a pincode")
      return
    }
    if (!validatePincode(trimmedPincode)) {
      toast.error("Please enter a valid 6-digit pincode")
      return
    }
    if (deliverablePincodes.includes(trimmedPincode)) {
      toast.error("This pincode is already added")
      return
    }
    setDeliverablePincodes((prev) => [...prev, trimmedPincode])
    setPincodeInput("")
    toast.success("Pincode added successfully")
  }, [pincodeInput, deliverablePincodes])

  const removePincode = useCallback((index: number) => {
    setDeliverablePincodes((prev) => prev.filter((_, i) => i !== index))
  }, [])

  if (isLoadingCategories) {
    return (
      <div className="flex items-center justify-center py-20 bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (allCategories.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">No Categories Available</h2>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">
            Please add some active categories before creating products.
          </p>
          <button
            onClick={() => navigate("/admin/categories")}
            className="bg-primary-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base"
          >
            Manage Categories
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/products")}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Products</span>
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Add New Product</h1>
              <p className="text-gray-600 mt-1">Create a new product for your store</p>
            </div>
          </div>
        </div>

        {isLoadingCategories ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 sm:space-y-8">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 sm:gap-8">
              {/* Main Content */}
              <div className="xl:col-span-2 space-y-4 sm:space-y-6">
                {/* Basic Information */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                      <input
                        {...register("name", {
                          required: "Product name is required",
                          minLength: { value: 2, message: "Product name must be at least 2 characters" },
                          maxLength: { value: 100, message: "Product name must be less than 100 characters" },
                        })}
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter product name"
                      />
                      {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                      <input
                        {...register("price", {
                          required: "Price is required",
                          min: { value: 1, message: "Price must be greater than 0" },
                          max: { value: 10000000, message: "Price seems too high" },
                        })}
                        type="number"
                        step="0.01"
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter price"
                      />
                      {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Original Price</label>
                      <input
                        {...register("originalPrice", {
                          min: { value: 1, message: "Original price must be greater than 0" },
                          validate: (value) => {
                            const price = watch("price")
                            if (value && price && value <= price) {
                              return "Original price should be greater than current price"
                            }
                            return true
                          },
                        })}
                        type="number"
                        step="0.01"
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter original price (optional)"
                      />
                      {errors.originalPrice && (
                        <p className="mt-1 text-sm text-red-600">{errors.originalPrice.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stock Quantity *</label>
                      <input
                        {...register("stock", {
                          required: "Stock quantity is required",
                          min: { value: 0, message: "Stock cannot be negative" },
                          max: { value: 100000, message: "Stock seems too high" },
                        })}
                        type="number"
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter stock quantity"
                        disabled={enableVariants}
                      />
                      {enableVariants && <p className="text-xs text-gray-500 mt-1">Stock is managed per variant</p>}
                      {errors.stock && <p className="mt-1 text-sm text-red-600">{errors.stock.message}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Material</label>
                      <input
                        {...register("material", { maxLength: { value: 50, message: "Material name too long" } })}
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="e.g., Solid Wood, Metal"
                      />
                      {errors.material && <p className="mt-1 text-sm text-red-600">{errors.material.message}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Warranty</label>
                      <input
                        {...register("warranty", {
                          maxLength: { value: 50, message: "Warranty description too long" },
                        })}
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="e.g., 2 years"
                      />
                      {errors.warranty && <p className="mt-1 text-sm text-red-600">{errors.warranty.message}</p>}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea
                        {...register("description", {
                          required: "Description is required",
                          minLength: { value: 10, message: "Description must be at least 10 characters" },
                          maxLength: { value: 1000, message: "Description must be less than 1000 characters" },
                        })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
                        placeholder="Enter product description"
                      />
                      {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Categories Selection */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories *</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Select one or more categories that best describe your product
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {allCategories.map((category) => {
                      const isSelected = selectedCategories.includes(category.slug)
                      return (
                        <div
                          key={category.id}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleCategoryToggle(category.slug)
                            const current = selectedCategories.includes(category.slug)
                              ? selectedCategories.filter((slug) => slug !== category.slug)
                              : [...selectedCategories, category.slug]
                            setValue("categories", current)
                          }}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-sm select-none ${
                            isSelected
                              ? "border-primary-500 bg-primary-50 text-primary-700 shadow-sm"
                              : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                          }`}
                          style={{ userSelect: "none" }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <div
                            className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
                              isSelected ? "border-primary-500 bg-primary-500" : "border-gray-300"
                            }`}
                          >
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium break-words">{category.name}</span>
                            <div className="text-xs text-gray-500 mt-1 break-all">Slug: {category.slug}</div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {errors.categories && <p className="mt-2 text-sm text-red-600">{errors.categories.message}</p>}
                  <input
                    {...register("categories", {
                      validate: (value) => {
                        if (!value || value.length === 0) return "Please select at least one category"
                        return true
                      },
                    })}
                    type="hidden"
                    value={selectedCategories.join(",")}
                    onChange={() => {}}
                  />
                  {selectedCategories.length > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-blue-900 mb-2">
                        Selected Categories ({selectedCategories.length}):
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedCategories.map((catSlug) => {
                          const category = allCategories.find((cat) => cat.slug === catSlug)
                          return (
                            <span
                              key={catSlug}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              <span className="break-all">{category?.name || catSlug}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleCategoryToggle(catSlug)
                                  const current = selectedCategories.filter((slug) => slug !== catSlug)
                                  setValue("categories", current)
                                }}
                                className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none flex-shrink-0"
                                onMouseDown={(e) => e.preventDefault()}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Wardrobe Variants</h3>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={enableWardrobeVariants}
                        onChange={(e) => {
                          setEnableWardrobeVariants(e.target.checked)
                          if (e.target.checked && enableVariants) {
                            setEnableVariants(false)
                            toast("Regular variants disabled - only one variant type allowed")
                          }
                        }}
                        className="accent-primary-600"
                      />
                      Enable Wardrobe Variants
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Use wardrobe variants for predefined sizes with specific pricing and custom size options.
                  </p>

                  {enableWardrobeVariants && (
                    <div className="mt-4 space-y-6">
                      {/* Custom Size Settings */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900">Custom Size Option</h4>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={enableCustomSize}
                              onChange={(e) => setEnableCustomSize(e.target.checked)}
                              className="accent-primary-600"
                            />
                            Enable Custom Size
                          </label>
                        </div>

                        {enableCustomSize && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Custom Size Price Multiplier (₹ per sq inch)
                            </label>
                            <input
                              type="number"
                              value={customSizeMultiplier}
                              onChange={(e) => setCustomSizeMultiplier(Number(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="1250"
                              min="1"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Price will be calculated as: Height × Width × {customSizeMultiplier}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Predefined Wardrobe Variants */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium text-gray-900">Predefined Sizes</h4>
                          <button
                            type="button"
                            onClick={addWardrobeVariant}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                          >
                            <Plus className="w-4 h-4" />
                            Add Size
                          </button>
                        </div>

                        <div className="space-y-3">
                          {wardrobeVariants.map((variant, index) => (
                            <div
                              key={variant.id}
                              className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded-lg"
                            >
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Size Name</label>
                                <input
                                  type="text"
                                  value={variant.name}
                                  onChange={(e) => updateWardrobeVariant(index, { name: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  placeholder="e.g., Small (2x3 ft)"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                                <input
                                  type="number"
                                  value={variant.price}
                                  onChange={(e) => updateWardrobeVariant(index, { price: Number(e.target.value) })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  placeholder="15000"
                                  min="1"
                                />
                              </div>
                              <div className="flex items-end justify-between">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={variant.isActive}
                                    onChange={(e) => updateWardrobeVariant(index, { isActive: e.target.checked })}
                                    className="accent-primary-600"
                                  />
                                  <span className="text-sm">Active</span>
                                </label>
                                <button
                                  type="button"
                                  onClick={() => removeWardrobeVariant(index)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Regular Variants */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Regular Variants</h3>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={enableVariants}
                        onChange={(e) => {
                          setEnableVariants(e.target.checked)
                          if (e.target.checked && enableWardrobeVariants) {
                            setEnableWardrobeVariants(false)
                            toast("Wardrobe variants disabled - only one variant type allowed")
                          }
                        }}
                        className="accent-primary-600"
                      />
                      Enable Regular Variants
                    </label>
                  </div>
                  {enableWardrobeVariants && (
                    <p className="text-sm text-orange-600 mt-2">
                      ⚠️ Wardrobe variants are enabled. Only one variant type can be active at a time.
                    </p>
                  )}

                  {enableVariants && (
                    <div className="mt-4 space-y-6">
                      {/* Sizes */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">Sizes</h4>
                          <button
                            type="button"
                            onClick={addSize}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                          >
                            <Plus className="w-4 h-4" />
                            Add Size
                          </button>
                        </div>
                        <div className="space-y-2">
                          {variantSizes.map((s, i) => (
                            <div key={i} className="p-3 border rounded-lg">
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={s.label}
                                  onChange={(e) => updateSizeLabel(i, e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  placeholder="e.g., Small, Medium, Large, XL, Custom"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeSize(i)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="mt-2">
                                <label className="inline-flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(s.dimensions)}
                                    onChange={() => toggleCustomDimensions(i)}
                                  />
                                  Custom dimensions
                                </label>
                                {s.dimensions && (
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                                    {(["length", "width", "height"] as const).map((k) => (
                                      <input
                                        key={k}
                                        type="number"
                                        min={0}
                                        step={0.1}
                                        value={(s.dimensions as any)[k] ?? ""}
                                        onChange={(e) =>
                                          updateSizeDimension(i, k, e.target.value ? Number(e.target.value) : undefined)
                                        }
                                        className="px-3 py-2 border rounded-lg"
                                        placeholder={`${k[0].toUpperCase() + k.slice(1)} (in)`}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Colors */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">Colors</h4>
                          <button
                            type="button"
                            onClick={addColor}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                          >
                            <Plus className="w-4 h-4" />
                            Add Color
                          </button>
                        </div>
                        <div className="space-y-4">
                          {variantColors.map((c, i) => (
                            <div key={i} className="p-3 border rounded-lg">
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Color Name</label>
                                  <input
                                    type="text"
                                    value={c.name}
                                    onChange={(e) => updateColor(i, { name: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    placeholder="e.g., Walnut, Teak, Black"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Hex</label>
                                  <input
                                    type="text"
                                    value={c.hex}
                                    onChange={(e) => updateColor(i, { hex: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    placeholder="#AABBCC"
                                  />
                                </div>
                                <div className="flex items-end justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Preview</span>
                                    <span
                                      className="inline-block w-6 h-6 rounded-full border"
                                      style={{ backgroundColor: c.hex || "#ccc" }}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeColor(i)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Color-specific images via URL */}
                              <div className="mt-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <ImageIcon className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm font-medium">Images for {c.name || "color"}</span>
                                  <span className="text-xs text-gray-500">
                                    (Optional - uses product images by default)
                                  </span>
                                </div>
                                <div className="flex gap-2">
                                  <input
                                    type="url"
                                    value={colorImageInputs[c.name] || ""}
                                    onChange={(e) =>
                                      setColorImageInputs((prev) => ({ ...prev, [c.name]: e.target.value }))
                                    }
                                    className="flex-1 px-3 py-2 border rounded-lg"
                                    placeholder="https://image-url.jpg (optional)"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => addColorImageUrl(c.name)}
                                    className="bg-primary-600 text-white px-3 rounded-lg hover:bg-primary-700"
                                  >
                                    Add
                                  </button>
                                </div>
                                {(c.images || []).length > 0 && (
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                                    {(c.images || []).map((url, idx) => (
                                      <div key={idx} className="relative">
                                        <img
                                          src={url || "/placeholder.svg"}
                                          alt={`${c.name} ${idx + 1}`}
                                          className="w-full h-20 object-cover rounded-lg border"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => removeColorImageUrl(c.name, idx)}
                                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {(c.images || []).length === 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    No color-specific images. Will use main product images by default.
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Variant Matrix */}
                      <div>
                        <h4 className="text-lg font-semibold mb-4">Variant Matrix</h4>
                        <div className="space-y-4">
                          {variants.map((v) => (
                            <div key={v.id} className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 border rounded-lg">
                              <div className="text-sm">
                                <div className="font-medium">Size</div>
                                <div>{v.size}</div>
                              </div>
                              <div className="text-sm">
                                <div className="font-medium">Color</div>
                                <div className="inline-flex items-center gap-2">
                                  <span
                                    className="inline-block w-3 h-3 rounded-full border"
                                    style={{ backgroundColor: v.colorHex || "#ccc" }}
                                  />
                                  <span>{v.color}</span>
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Price Modifier (₹)</label>
                                <input
                                  type="number"
                                  value={v.priceModifier || 0}
                                  onChange={(e) => updateVariantField(v.id, "priceModifier", Number(e.target.value))}
                                  className="w-full px-2 py-1 border rounded"
                                />
                                <div className="text-xs text-gray-600 mt-1">
                                  Final: ₹{((watch("price") || 0) + (v.priceModifier || 0)).toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Stock</label>
                                <input
                                  type="number"
                                  min={0}
                                  value={v.stock || 0}
                                  onChange={(e) => updateVariantField(v.id, "stock", Number(e.target.value))}
                                  className="w-full px-2 py-1 border rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">SKU</label>
                                <input
                                  type="text"
                                  value={v.sku || ""}
                                  onChange={(e) => updateVariantField(v.id, "sku", e.target.value)}
                                  className="w-full px-2 py-1 border rounded"
                                />
                              </div>
                              <div className="flex items-center">
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={v.isActive !== false}
                                    onChange={(e) => updateVariantField(v.id, "isActive", e.target.checked)}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">Active</span>
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-4 sm:space-y-6">
                {/* Image URLs */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Product Images *</h3>
                    <button
                      type="button"
                      onClick={addImageUrl}
                      disabled={imageUrls.length >= 5}
                      className="text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors p-1"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {imageUrls.map((url, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <input
                            type="url"
                            value={url}
                            onChange={(e) => updateImageUrl(index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm"
                            placeholder="Enter image URL (https://...)"
                            required={index === 0}
                          />
                          <button
                            type="button"
                            onClick={() => removeImageUrl(index)}
                            disabled={imageUrls.length <= 1}
                            className="text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none p-1 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {url && validateImageUrl(url) && (
                          <div
                            className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 ${
                              mainImageIndex === index
                                ? "border-primary-500 ring-2 ring-primary-200"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            onClick={() => setAsMainImage(index)}
                          >
                            <img
                              src={url || "/placeholder.svg"}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 sm:h-32 object-cover"
                              onError={(e) => {
                                ;(e.currentTarget as HTMLImageElement).style.display = "none"
                              }}
                              loading={index === 0 ? "eager" : "lazy"}
                            />
                            {mainImageIndex === index && (
                              <div className="absolute top-2 left-2 bg-primary-500 text-white px-2 py-1 rounded text-xs font-medium flex items-center">
                                <Star className="w-3 h-3 inline mr-1" />
                                Main
                              </div>
                            )}
                          </div>
                        )}

                        {url && !validateImageUrl(url) && (
                          <p className="text-sm text-red-600">
                            Invalid image URL. Please use a valid URL ending with .jpg, .jpeg, .png, .gif, .webp, or
                            .svg
                          </p>
                        )}
                      </div>
                    ))}

                    <p className="text-xs text-gray-500">
                      Add up to 5 image URLs. Click on an image to set it as the main image. Use high-quality images
                      (recommended: 800x600px or larger).
                    </p>
                  </div>
                </div>

                {/* Status */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Status</h3>

                  <div className="space-y-4">
                    <label className="flex items-start cursor-pointer">
                      <input
                        {...register("isFeatured")}
                        type="checkbox"
                        className="mt-1 mr-3 text-primary-600 focus:ring-primary-500 rounded flex-shrink-0"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700">Featured Product</span>
                        <p className="text-xs text-gray-500">Show in featured sections</p>
                      </div>
                    </label>

                    <label className="flex items-start cursor-pointer">
                      <input
                        {...register("isNewArrival")}
                        type="checkbox"
                        className="mt-1 mr-3 text-primary-600 focus:ring-primary-500 rounded flex-shrink-0"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700">New Arrival</span>
                        <p className="text-xs text-gray-500">Show in new arrivals section</p>
                      </div>
                    </label>

                    <label className="flex items-start cursor-pointer">
                      <input
                        {...register("isBestSeller")}
                        type="checkbox"
                        className="mt-1 mr-3 text-primary-600 focus:ring-primary-500 rounded flex-shrink-0"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700">Best Seller</span>
                        <p className="text-xs text-gray-500">Show in best sellers section</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Delivery Zones */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Zones *</h3>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deliverable Pincodes</label>

                  <div className="flex flex-wrap gap-2 mb-3 max-h-24 overflow-y-auto border border-gray-100 rounded p-2 bg-gray-50">
                    {deliverablePincodes.map((pincode, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium"
                      >
                        <span className="break-all">{pincode}</span>
                        <button
                          type="button"
                          className="ml-1 text-primary-600 hover:text-red-600 focus:outline-none transition-colors flex-shrink-0"
                          onClick={() => removePincode(idx)}
                          aria-label={`Remove pincode ${pincode}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>

                  {deliverablePincodes.length === 0 && (
                    <p className="text-sm text-red-600 mb-3">Please add at least one pincode</p>
                  )}

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={pincodeInput}
                      onChange={(e) => setPincodeInput(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                      maxLength={6}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter 6-digit pincode"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          addPincode()
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="bg-primary-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex-shrink-0"
                      onClick={addPincode}
                      disabled={pincodeInput.length !== 6}
                    >
                      Add
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    Add pincodes where this product can be delivered. You have {deliverablePincodes.length} pincode(s)
                    added.
                  </p>
                </div>

                {/* Actions */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <div className="space-y-3">
                    <button
                      type="submit"
                      disabled={isLoading || allCategories.length === 0}
                      className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Adding Product...</span>
                        </div>
                      ) : (
                        "Add Product"
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate("/admin/products")}
                      disabled={isLoading}
                      className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      Cancel
                    </button>
                  </div>

                  {/* Form validation summary */}
                  <div className="mt-4 text-xs text-gray-500">
                    <p className="font-medium mb-1">Requirements:</p>
                    <ul className="space-y-1 ml-2">
                      <li className={selectedCategories.length > 0 ? "text-green-600" : "text-red-600"}>
                        ✓ At least one category
                      </li>
                      <li
                        className={imageUrls.some((url) => validateImageUrl(url)) ? "text-green-600" : "text-red-600"}
                      >
                        ✓ At least one valid image
                      </li>
                      <li className={deliverablePincodes.length > 0 ? "text-green-600" : "text-red-600"}>
                        ✓ At least one pincode
                      </li>
                      {enableVariants && (
                        <>
                          <li className={variantSizes.length > 0 ? "text-green-600" : "text-red-600"}>
                            ✓ At least one size
                          </li>
                          <li className={variantColors.length > 0 ? "text-green-600" : "text-red-600"}>
                            ✓ At least one color
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Adding Product...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Add Product
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default AddProduct
